import { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Spin } from 'antd';
import styled from 'styled-components';

import type { IModelInstance } from '../../../ui-builder';
import { resolveInstanceName } from '../utils/epiHelpers';
import type { SupportedLanguage } from '../../../translate';

export interface IAddIdmpTab {
  key: string;
  label: string;
  /** Live (non-deleted) compositions in that tab. */
  compositions: { id: string; title: string }[];
}

interface IProps {
  isOpen: boolean;
  /** Candidate IDMPs — the family's members not already on the ePI. */
  candidates: IModelInstance[];
  isLoading: boolean;
  tabs: IAddIdmpTab[];
  /** The language being authored — product names resolve in it. */
  lang: SupportedLanguage;
  onConfirm: (
    idmpUuid: string,
    compositionIdByTab: Record<string, string>
  ) => void;
  onCancel: () => void;
}

/**
 * Adds one of the family's IDMPs to the ePI.
 *
 * Every IDMP must sit in exactly one composition per document type, so the new
 * one has to be placed in each tab straight away — a tab with a single
 * composition has no choice to make and just shows where it will land.
 */
const AddIdmpModal: React.FC<IProps> = ({
  isOpen,
  candidates,
  isLoading,
  tabs,
  lang,
  onConfirm,
  onCancel,
}) => {
  const [idmpUuid, setIdmpUuid] = useState<string | null>(null);
  const [targets, setTargets] = useState<Record<string, string>>({});

  const defaultTargets = useMemo(
    () =>
      Object.fromEntries(
        tabs
          .filter(t => t.compositions.length > 0)
          .map(t => [t.key, t.compositions[0].id])
      ),
    [tabs]
  );

  useEffect(() => {
    if (!isOpen) return;
    setIdmpUuid(null);
    setTargets(defaultTargets);
  }, [isOpen, defaultTargets]);

  const options = useMemo(
    () =>
      candidates.map(inst => ({
        value: inst.id,
        label: resolveInstanceName(inst.data?.name, lang) ?? inst.id,
      })),
    [candidates, lang]
  );

  const everyTabTargeted = tabs.every(t => !!targets[t.key]);
  const valid = !!idmpUuid && everyTabTargeted;

  return (
    <Modal
      open={isOpen}
      title="Add a medicinal product"
      okText="Add"
      cancelText="Cancel"
      okButtonProps={{ disabled: !valid }}
      onOk={() => valid && idmpUuid && onConfirm(idmpUuid, targets)}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Description>
        Choose a product from the family. It has to belong to exactly one
        document in every tab.
      </Description>

      {isLoading ? (
        <Spin />
      ) : options.length === 0 ? (
        <Hint>
          No products available — every product in this family is already on
          this ePI.
        </Hint>
      ) : (
        <>
          <Field>
            <Label>Medicinal product</Label>
            <Select
              options={options}
              value={idmpUuid ?? undefined}
              onChange={setIdmpUuid}
              placeholder="Select a product"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
            />
          </Field>

          {tabs.map(tab => (
            <Field key={tab.key}>
              <Label>{tab.label}</Label>
              {tab.compositions.length > 1 ? (
                <Select
                  options={tab.compositions.map(c => ({
                    value: c.id,
                    label: c.title,
                  }))}
                  value={targets[tab.key]}
                  onChange={v => setTargets(t => ({ ...t, [tab.key]: v }))}
                  style={{ width: '100%' }}
                />
              ) : (
                <Fixed>{tab.compositions[0]?.title ?? '—'}</Fixed>
              )}
            </Field>
          ))}
        </>
      )}
    </Modal>
  );
};

export default AddIdmpModal;

const Description = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: #4b576a;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #4b576a;
`;

const Fixed = styled.div`
  font-size: 13px;
  padding: 5px 12px;
  background: #f5f5f5;
  border-radius: 6px;
`;

const Hint = styled.div`
  font-size: 13px;
  color: #ad6800;
`;
